import { PublishRegisterCommand } from "commands/publish/PublishRegisterCommand";
import { CreateSetCommand } from "commands/publish/CreateSetCommand";
import { UpdateSetCommand } from "commands/publish/UpdateSetCommand";
import { CreatePromptCommand } from "commands/publish/CreatePromptCommand";
import { UpdatePromptCommand } from "commands/publish/UpdatePromptCommand";
import { SortPromptsCommand } from "commands/publish/SortPromptsCommand";
import { GetProfilePublishResult } from "results/publish/GetProfilePublishResult";
import { GetCreateSetResult } from "results/publish/GetCreateDataResult";
import { GetProfilePublishSetResult } from "results/publish/GetProfilePublishSetResult";
import { GetUpdateSetResult } from "results/publish/GetUpdateSetResult";
import { GetUpdatePromptResult } from "results/publish/GetUpdatePromptResult";
import { GetProfilePublishSetImportResult } from "results/publish/GetProfilePublishSetImportResult";
import { GetUploadedImagesResult } from "results/publish/GetUploadedImagesResult";
import { GetGalleryImagesResult } from "results/publish/GetGalleryImagesResult";
import { SearchGalleryImagesResult } from "results/publish/SearchGalleryImagesResult";
import { useApiService } from "services/ApiService";
import { CommandResult } from "results/CommandResult";
import { IdResult } from "results/IdResult";
import { SuccessResult } from "results/SuccessResult";

export const PublishApiService = () => {
    const { get, post, getUrl, getHeaders } = useApiService({ controller: "/publish" });

    return {
        getProfile: () => get<GetProfilePublishResult>("/profile"),
        getProfilePublishSet: (id: number) => get<GetProfilePublishSetResult>(`/set/${id}`),
        getProfilePublishSetImport: (id: number) => get<GetProfilePublishSetImportResult>(`/set/${id}/import`),
        getUpdateSet: (id: number) => get<GetUpdateSetResult>(`/update-set/${id}`),
        checkPublisherName: (name: string) =>
            get<SuccessResult>(`/publisher-name-available?name=${encodeURIComponent(name)}`),
        register: async (command: PublishRegisterCommand) => {
            const formData = new FormData();
            formData.append("name", command.name);

            if (command.description) {
                formData.append("description", command.description);
            }

            if (command.image) {
                formData.append("image", command.image);
            }

            const response = await fetch(getUrl("/register"), {
                method: "POST",
                body: formData,
                headers: getHeaders(false),
            });

            return await response.json();
        },
        updateProfile: async (command: PublishRegisterCommand) => {
            const formData = new FormData();
            formData.append("name", command.name);

            if (command.description) {
                formData.append("description", command.description);
            }

            if (command.image) {
                formData.append("image", command.image);
            }

            if (command.imageId) {
                formData.append("imageId", command.imageId.toString());
            }

            const response = await fetch(getUrl("/update-profile"), {
                method: "POST",
                body: formData,
                headers: getHeaders(false),
            });

            return await response.json();
        },
        createSet: async (command: CreateSetCommand) => {
            const formData = new FormData();
            formData.append("name", command.name);
            formData.append("language", command.language);
            formData.append("isPublic", command.isPublic.toString());

            if (command.description) {
                formData.append("description", command.description);
            }

            if (command.categoryId) {
                formData.append("categoryId", command.categoryId.toString());
            }

            if (command.tags) {
                formData.append("tags", command.tags);
            }

            command.schoolLevelIds?.forEach((id, index) => {
                formData.append(`schoolLevelIds[${index}]`, id.toString());
            });

            if (command.image) {
                formData.append("image", command.image);
            }

            if (command.imageId) {
                formData.append("imageId", command.imageId.toString());
            }

            const response = await fetch(getUrl("/create-set"), {
                method: "POST",
                body: formData,
                headers: getHeaders(false),
            });

            return await (response.json() as Promise<CommandResult<IdResult>>);
        },
        updateSet: async (command: UpdateSetCommand) => {
            const formData = new FormData();
            formData.append("id", command.id.toString());
            formData.append("name", command.name);
            formData.append("language", command.language);
            formData.append("isPublic", command.isPublic.toString());

            if (command.description) {
                formData.append("description", command.description);
            }

            if (command.categoryId) {
                formData.append("categoryId", command.categoryId.toString());
            }

            if (command.tags) {
                formData.append("tags", command.tags);
            }

            command.schoolLevelIds?.forEach((id, index) => {
                formData.append(`schoolLevelIds[${index}]`, id.toString());
            });

            if (command.image) {
                formData.append("image", command.image);
            }

            if (command.imageId) {
                formData.append("imageId", command.imageId.toString());
            }

            const response = await fetch(getUrl("/update-set"), {
                method: "POST",
                body: formData,
                headers: getHeaders(false),
            });

            return await response.json();
        },
        getCreateSetData: () => get<GetCreateSetResult>("/create-set-data"),
        deletePublisher: () => post("/delete-publisher"),
        deleteSet: (id: number) => post(`/delete-set/${id}`),
        createPrompt: async (command: CreatePromptCommand) => {
            const formData = new FormData();

            formData.append("name", command.name);
            formData.append("promptText", command.promptText);
            formData.append("topicId", command.topicId.toString());
            formData.append("language", command.language);

            if (command.imageId) {
                formData.append("imageId", command.imageId.toString());
            }

            if (command.settingWritingTimer) {
                formData.append("settingWritingTimer", command.settingWritingTimer.toString());
            }

            if (command.settingVotingTimer) {
                formData.append("settingVotingTimer", command.settingVotingTimer.toString());
            }

            if (command.settingCharacterLimit) {
                formData.append("settingCharacterLimit", command.settingCharacterLimit.toString());
            }

            // Add round instructions
            command.roundInstructions
                ?.filter(x => x.name)
                .forEach((instruction, index) => {
                    formData.append(`roundInstructions[${index}].roundNumber`, instruction.roundNumber.toString());
                    formData.append(`roundInstructions[${index}].name`, instruction.name);
                });

            if (command.image) {
                formData.append("image", command.image);
            }

            const response = await fetch(getUrl("/create-prompt"), {
                method: "POST",
                body: formData,
                headers: getHeaders(false),
            });

            return await response.json();
        },
        deletePrompt: (id: number) => post(`/delete-prompt/${id}`),
        sortPrompts: (command: SortPromptsCommand) => post("/sort-prompts", command),
        getUploadedImages: () => get<GetUploadedImagesResult>("/uploaded-images"),
        getGalleryImages: () => get<GetGalleryImagesResult>("/gallery-images"),
        searchGalleryImages: (themeId?: number, searchTerm?: string) => {
            const params = new URLSearchParams();
            if (themeId !== undefined) {
                params.append("themeId", themeId.toString());
            }
            if (searchTerm) {
                params.append("searchTerm", searchTerm);
            }
            return get<SearchGalleryImagesResult>(`/search-gallery-images?${params.toString()}`);
        },
        getUpdatePrompt: (id: number) => get<GetUpdatePromptResult>(`/update-prompt/${id}`),
        updatePrompt: async (command: UpdatePromptCommand) => {
            const formData = new FormData();

            // do not post the topic id

            formData.append("id", command.id.toString());
            formData.append("name", command.name);
            formData.append("promptText", command.promptText);
            formData.append("language", command.language);

            if (command.imageId) {
                formData.append("imageId", command.imageId.toString());
            }

            if (command.settingWritingTimer) {
                formData.append("settingWritingTimer", command.settingWritingTimer.toString());
            }

            if (command.settingVotingTimer) {
                formData.append("settingVotingTimer", command.settingVotingTimer.toString());
            }

            if (command.settingCharacterLimit) {
                formData.append("settingCharacterLimit", command.settingCharacterLimit.toString());
            }

            // Add round instructions
            command.roundInstructions
                ?.filter(x => x.name)
                .forEach((instruction, index) => {
                    formData.append(`roundInstructions[${index}].roundNumber`, instruction.roundNumber.toString());
                    formData.append(`roundInstructions[${index}].name`, instruction.name);
                });

            if (command.image) {
                formData.append("image", command.image);
            }

            const response = await fetch(getUrl("/update-prompt"), {
                method: "POST",
                body: formData,
                headers: getHeaders(false),
            });

            return await response.json();
        },
    };
};
